import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Briefcase,
  Search,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Building2,
  Users,
  Calendar,
  Play,
  Pause,
  Trash2,
  CheckSquare,
  Square,
  X
} from "lucide-react";

const AdminJobs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [total, setTotal] = useState(0);
  
  // Bulk selection state
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchJobs();
  }, [user]);

  const fetchJobs = async () => {
    setLoading(true);
    setSelectedJobs([]); // Clear selection on refresh
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "100");

      const res = await fetch(`${API}/admin/jobs?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    try {
      const res = await fetch(`${API}/admin/jobs/${jobId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(`Job ${newStatus === "active" ? "activated" : newStatus}!`);
        fetchJobs();
      } else {
        toast.error("Failed to update job status");
      }
    } catch (error) {
      toast.error("Failed to update job status");
    }
  };

  const deleteJob = async (jobId) => {
    if (!confirm("Are you sure you want to delete this job? This action cannot be undone.")) {
      return;
    }
    try {
      const res = await fetch(`${API}/admin/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Job deleted successfully!");
        fetchJobs();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete job");
      }
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  // Bulk selection handlers
  const toggleSelectJob = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs.map(j => j.job_id));
    }
  };

  const clearSelection = () => {
    setSelectedJobs([]);
  };

  // Bulk action handlers
  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/jobs/bulk/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ job_ids: selectedJobs })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Successfully deleted ${data.jobs_deleted} jobs and ${data.applications_deleted} applications`);
        setShowBulkConfirm(null);
        fetchJobs();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete jobs");
      }
    } catch (error) {
      toast.error("Failed to delete jobs");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    setBulkActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/jobs/bulk/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ job_ids: selectedJobs, status: newStatus })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Successfully updated ${data.jobs_updated} jobs to "${newStatus}"`);
        setShowBulkConfirm(null);
        fetchJobs();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update jobs");
      }
    } catch (error) {
      toast.error("Failed to update jobs");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-green-500/20 text-green-400";
      case "suspended": return "bg-red-500/20 text-red-400";
      case "closed": return "bg-gray-500/20 text-gray-400";
      case "draft": return "bg-yellow-500/20 text-yellow-400";
      default: return "bg-gray-500/20 text-gray-400";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active": return CheckCircle;
      case "suspended": return XCircle;
      case "closed": return Clock;
      default: return Clock;
    }
  };

  const isAllSelected = jobs.length > 0 && selectedJobs.length === jobs.length;
  const isSomeSelected = selectedJobs.length > 0 && selectedJobs.length < jobs.length;

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2">
                JOB <span className="text-crimson">MANAGEMENT</span>
              </h1>
              <p className="text-gray-400 text-sm">Monitor and manage all job postings</p>
            </div>
            <Button onClick={fetchJobs} variant="outline" className="border-[#333] text-white w-fit">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Briefcase className="w-4 h-4" />
                <span className="text-xs">Total Jobs</span>
              </div>
              <div className="text-2xl font-bold">{total}</div>
            </div>
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs">Active</span>
              </div>
              <div className="text-2xl font-bold text-green-400">
                {jobs.filter(j => j.status === "active").length}
              </div>
            </div>
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="text-xs">Suspended</span>
              </div>
              <div className="text-2xl font-bold text-red-400">
                {jobs.filter(j => j.status === "suspended").length}
              </div>
            </div>
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs">Closed</span>
              </div>
              <div className="text-2xl font-bold text-gray-400">
                {jobs.filter(j => j.status === "closed").length}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 flex-wrap mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchJobs()}
                className="pl-10 bg-obsidian-light border-[#333] text-white"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-obsidian-light border border-[#333] text-white"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="closed">Closed</option>
              <option value="draft">Draft</option>
            </select>
            <Button onClick={fetchJobs} className="bg-crimson hover:bg-crimson/90">
              Search
            </Button>
          </div>

          {/* Bulk Actions Bar */}
          {selectedJobs.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-crimson/10 border border-crimson/30 p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <span className="text-crimson font-semibold">
                  {selectedJobs.length} job{selectedJobs.length > 1 ? 's' : ''} selected
                </span>
                <button 
                  onClick={clearSelection}
                  className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
                >
                  <X className="w-4 h-4" /> Clear
                </button>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                  onClick={() => setShowBulkConfirm({ action: 'activate', status: 'active' })}
                  data-testid="bulk-activate-btn"
                >
                  <Play className="w-4 h-4 mr-1" /> Activate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                  onClick={() => setShowBulkConfirm({ action: 'suspend', status: 'suspended' })}
                  data-testid="bulk-suspend-btn"
                >
                  <Pause className="w-4 h-4 mr-1" /> Suspend
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-gray-500/50 text-gray-400 hover:bg-gray-500/10"
                  onClick={() => setShowBulkConfirm({ action: 'close', status: 'closed' })}
                  data-testid="bulk-close-btn"
                >
                  <Clock className="w-4 h-4 mr-1" /> Close
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setShowBulkConfirm({ action: 'delete' })}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </motion.div>
          )}

          {/* Jobs Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">No jobs found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select All Header */}
              <div className="flex items-center gap-3 px-2 py-2 bg-obsidian-light/50 border border-[#222]">
                <button
                  onClick={toggleSelectAll}
                  className="p-1 hover:bg-white/5 rounded transition-colors"
                  data-testid="select-all-checkbox"
                >
                  {isAllSelected ? (
                    <CheckSquare className="w-5 h-5 text-crimson" />
                  ) : isSomeSelected ? (
                    <div className="w-5 h-5 border-2 border-crimson bg-crimson/30 flex items-center justify-center">
                      <div className="w-2 h-0.5 bg-crimson" />
                    </div>
                  ) : (
                    <Square className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                <span className="text-sm text-gray-400">
                  {isAllSelected ? 'Deselect all' : 'Select all'} ({jobs.length} jobs)
                </span>
              </div>

              {jobs.map((job) => {
                const StatusIcon = getStatusIcon(job.status);
                const isSelected = selectedJobs.includes(job.job_id);
                
                return (
                  <motion.div
                    key={job.job_id}
                    className={`bg-obsidian-light border p-4 sm:p-6 transition-colors ${
                      isSelected ? 'border-crimson/50 bg-crimson/5' : 'border-[#222] hover:border-[#333]'
                    }`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleSelectJob(job.job_id)}
                        className="p-1 hover:bg-white/5 rounded transition-colors mt-1 flex-shrink-0"
                        data-testid={`select-job-${job.job_id}`}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-crimson" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="font-heading text-lg font-bold text-white truncate">
                                {job.title}
                              </h3>
                              <span className={`px-2 py-1 text-xs uppercase flex items-center gap-1 flex-shrink-0 ${getStatusColor(job.status)}`}>
                                <StatusIcon className="w-3 h-3" />
                                {job.status}
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{job.company || "Unknown"}</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{job.location || "Remote"}</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4 flex-shrink-0" />
                                {job.applicants?.length || 0} applicants
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                {job.created_at ? new Date(job.created_at).toLocaleDateString() : "-"}
                              </span>
                            </div>
                            
                            {job.recruiter && (
                              <p className="text-sm text-gray-500 mt-2 truncate">
                                Posted by: {job.recruiter.name || job.recruiter.email}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#333] text-white"
                              onClick={() => navigate(`/jobs/${job.job_id}`)}
                            >
                              <Eye className="w-4 h-4 sm:mr-2" />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                            
                            {job.status === "active" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                onClick={() => updateJobStatus(job.job_id, "suspended")}
                              >
                                <Pause className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Suspend</span>
                              </Button>
                            )}
                            
                            {job.status === "suspended" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                                onClick={() => updateJobStatus(job.job_id, "active")}
                              >
                                <Play className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Activate</span>
                              </Button>
                            )}
                            
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => deleteJob(job.job_id)}
                              data-testid={`delete-job-${job.job_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Bulk Action Confirmation Modal */}
      {showBulkConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-obsidian-light border border-[#222] p-6 max-w-md w-full"
          >
            <h3 className="font-heading text-xl font-bold mb-4">
              Confirm Bulk {showBulkConfirm.action === 'delete' ? 'Delete' : 'Status Update'}
            </h3>
            
            <p className="text-gray-400 mb-6">
              {showBulkConfirm.action === 'delete' ? (
                <>
                  Are you sure you want to <span className="text-red-400 font-semibold">permanently delete</span>{' '}
                  {selectedJobs.length} job{selectedJobs.length > 1 ? 's' : ''}? 
                  This will also remove all associated applications. This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to change the status of {selectedJobs.length} job{selectedJobs.length > 1 ? 's' : ''} to{' '}
                  <span className={`font-semibold ${
                    showBulkConfirm.status === 'active' ? 'text-green-400' :
                    showBulkConfirm.status === 'suspended' ? 'text-yellow-400' :
                    'text-gray-400'
                  }`}>
                    {showBulkConfirm.status}
                  </span>?
                </>
              )}
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                className="border-[#333] text-white"
                onClick={() => setShowBulkConfirm(null)}
                disabled={bulkActionLoading}
              >
                Cancel
              </Button>
              <Button
                className={showBulkConfirm.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-crimson hover:bg-crimson/90'}
                onClick={() => {
                  if (showBulkConfirm.action === 'delete') {
                    handleBulkDelete();
                  } else {
                    handleBulkStatusUpdate(showBulkConfirm.status);
                  }
                }}
                disabled={bulkActionLoading}
                data-testid="confirm-bulk-action-btn"
              >
                {bulkActionLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : null}
                {showBulkConfirm.action === 'delete' ? 'Delete Jobs' : 'Update Status'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminJobs;
